/*
 * AI 填表员 · 首页引导 — 自动轮播 Hero
 *
 * 展示 AI 填好的多种文档示例（Excel 表格 + Word 汇报），证明覆盖面：
 *   - 自动轮播（3500ms）
 *   - 格式标签页跳转 / 圆点 / ‹ › 箭头切换
 */

import React, { useEffect, useMemo, useState } from 'react'
import {
  CheckCircleOutlined,
  FileTextOutlined,
  LeftOutlined,
  PictureOutlined,
  RightOutlined,
  TableOutlined
} from '@ant-design/icons'
import './HeroCarousel.scss'

type SlideFormat = 'excel' | 'word' | 'photo'

type CellTone = 'green' | 'amber' | 'plain'

interface ExcelCell {
  text: string
  tone?: CellTone
  photo?: boolean
}

interface ExcelSlide {
  type: 'excel'
  file: string
  chip: string
  status: string
  cols: string[]
  rows: ExcelCell[][]
}

interface WordBlock {
  heading: string
  bars: number[] // bar widths in %
  photos?: number // count of inline photo thumbs
  photoCaption?: string
  tag?: string // small amber tag, e.g. 待整改
}

interface WordSlide {
  type: 'word'
  file: string
  chip: string
  status: string
  title: string
  meta: string
  blocks: WordBlock[]
  signature: string
}

interface PhotoItem {
  location: string
  time: string
  tag?: string
}

interface PhotoSlide {
  type: 'photo'
  file: string
  chip: string
  status: string
  title: string
  items: PhotoItem[]
}

type Slide = ExcelSlide | WordSlide | PhotoSlide

interface FormatTab {
  format: SlideFormat
  label: string
  icon: React.ComponentType<any>
}

const TABS: FormatTab[] = [
  { format: 'excel', label: 'Excel 表格', icon: TableOutlined },
  { format: 'word', label: 'Word 汇报', icon: FileTextOutlined },
  { format: 'photo', label: '照片拼图', icon: PictureOutlined }
]

const SLIDES: Slide[] = [
  {
    type: 'excel',
    file: '工地巡检报告.xlsx',
    chip: 'AI 已填好',
    status: 'AI 已填好 · 逐项配现场照片',
    cols: ['序号', '点位', '检查项', '状态', '照片'],
    rows: [
      [
        { text: '1' },
        { text: '基坑边坡' },
        { text: '支护稳定' },
        { text: '正常', tone: 'green' },
        { text: '', photo: true }
      ],
      [
        { text: '2' },
        { text: '临边防护' },
        { text: '护栏完好' },
        { text: '正常', tone: 'green' },
        { text: '', photo: true }
      ],
      [
        { text: '3' },
        { text: '配电箱' },
        { text: '接地标识' },
        { text: '待整改', tone: 'amber' },
        { text: '', photo: true }
      ],
      [
        { text: '4' },
        { text: '模板支架' },
        { text: '立杆间距' },
        { text: '正常', tone: 'green' },
        { text: '', photo: true }
      ],
      [
        { text: '5' },
        { text: '卸料平台' },
        { text: '防护到位' },
        { text: '正常', tone: 'green' },
        { text: '', photo: true }
      ],
      [
        { text: '6' },
        { text: '施工电梯' },
        { text: '限位有效' },
        { text: '待整改', tone: 'amber' },
        { text: '', photo: true }
      ],
      [
        { text: '7' },
        { text: '消防器材' },
        { text: '配置齐全' },
        { text: '正常', tone: 'green' },
        { text: '', photo: true }
      ]
    ]
  },
  {
    type: 'excel',
    file: '到货验收记录.xlsx',
    chip: 'AI 已填好',
    status: 'AI 已填好 · 数据来自现场照片',
    cols: ['序号', '照片', '材料名称', '验收时间', '结果'],
    rows: [
      [
        { text: '1' },
        { text: '', photo: true },
        { text: '螺纹钢 HRB400' },
        { text: '04-17 09:12' },
        { text: '合格', tone: 'green' }
      ],
      [
        { text: '2' },
        { text: '', photo: true },
        { text: '商砼 C30' },
        { text: '04-17 10:05' },
        { text: '合格', tone: 'green' }
      ],
      [
        { text: '3' },
        { text: '', photo: true },
        { text: '防水卷材 SBS' },
        { text: '04-17 11:20' },
        { text: '待复检', tone: 'amber' }
      ],
      [
        { text: '4' },
        { text: '', photo: true },
        { text: '加气块 B06' },
        { text: '04-17 13:40' },
        { text: '合格', tone: 'green' }
      ],
      [
        { text: '5' },
        { text: '', photo: true },
        { text: '覆膜模板' },
        { text: '04-17 14:15' },
        { text: '合格', tone: 'green' }
      ],
      [
        { text: '6' },
        { text: '', photo: true },
        { text: '电缆 YJV-4×25' },
        { text: '04-17 15:02' },
        { text: '待复检', tone: 'amber' }
      ],
      [
        { text: '7' },
        { text: '', photo: true },
        { text: '防火涂料' },
        { text: '04-17 15:50' },
        { text: '合格', tone: 'green' }
      ]
    ]
  },
  {
    type: 'excel',
    file: '车辆油耗台账.xlsx',
    chip: 'AI 已填好',
    status: 'AI 已填好 · 数据来自加油单照片',
    cols: ['加油日期', '加油前公里', '升数', '金额', '油耗'],
    rows: [
      [
        { text: '04-03' },
        { text: '38216 km' },
        { text: '42.5 L' },
        { text: '¥386' },
        { text: '8.9' }
      ],
      [
        { text: '04-12' },
        { text: '38690 km' },
        { text: '39.8 L' },
        { text: '¥362' },
        { text: '8.4' }
      ],
      [
        { text: '04-21' },
        { text: '39105 km' },
        { text: '41.2 L' },
        { text: '¥375' },
        { text: '8.7' }
      ],
      [
        { text: '04-26' },
        { text: '39520 km' },
        { text: '40.6 L' },
        { text: '¥369' },
        { text: '8.6' }
      ],
      [
        { text: '05-04' },
        { text: '39988 km' },
        { text: '43.1 L' },
        { text: '¥391' },
        { text: '8.8' }
      ],
      [
        { text: '05-13' },
        { text: '40402 km' },
        { text: '38.9 L' },
        { text: '¥354' },
        { text: '8.2' }
      ],
      [
        { text: '05-22' },
        { text: '40861 km' },
        { text: '41.7 L' },
        { text: '¥379' },
        { text: '8.5' }
      ]
    ]
  },
  {
    type: 'word',
    file: '施工日报.docx',
    chip: 'AI 已写好',
    status: 'AI 已写好 · 照片与数据自动成文',
    title: '施工日报',
    meta: '日期 2026-04-17 · 天气 晴 · 气温 12~22℃ · 记录人 张伟',
    signature: '记录人（签字）：张伟',
    blocks: [
      {
        heading: '一、生产情况',
        bars: [100, 92, 84],
        photos: 2,
        photoCaption: '现场照片'
      },
      {
        heading: '二、安全质量',
        bars: [100, 88]
      }
    ]
  },
  {
    type: 'word',
    file: '监理日报.docx',
    chip: 'AI 已写好',
    status: 'AI 已写好 · 自动归纳当日要点',
    title: '监理日报',
    meta: '日期 2026-04-17 · 工程 市政道路工程 · 天气 晴 · 监理 项目监理部',
    signature: '总监理工程师（签字）：',
    blocks: [
      {
        heading: '一、监理工作情况',
        bars: [100, 90, 82]
      },
      {
        heading: '二、旁站记录',
        bars: [100, 86],
        photos: 1,
        photoCaption: '旁站照片'
      },
      {
        heading: '三、存在问题与处理',
        bars: [96, 80],
        tag: '待整改'
      }
    ]
  },
  {
    type: 'photo',
    file: '现场巡检拼图.docx',
    chip: 'AI 已排好',
    status: 'AI 已排好 · 6 张照片自动配文',
    title: '2026-04-17 主变区巡检',
    items: [
      { location: '1# 主变压器区', time: '09:12' },
      { location: '2# 母线桥接头', time: '09:48', tag: '待整改' },
      { location: '3# 配电间', time: '10:25' },
      { location: '4# 电缆层', time: '11:02' },
      { location: '5# 消防器材', time: '11:37' },
      { location: '6# 卸料平台', time: '12:08' }
    ]
  },
  {
    type: 'photo',
    file: '到货验收拼图.docx',
    chip: 'AI 已排好',
    status: 'AI 已排好 · 4 张照片自动排版',
    title: '2026-04-17 到货验收',
    items: [
      { location: '螺纹钢 HRB400', time: '09:12' },
      { location: '商砼 C30', time: '10:05' },
      { location: '防水卷材 SBS', time: '11:20', tag: '待复检' },
      { location: '加气块 B06', time: '13:40' }
    ]
  }
]

const B = 'aiClerk-carousel'
const COUNT = SLIDES.length
const AUTO_MS = 3500

function mod(n: number, m: number): number {
  return ((n % m) + m) % m
}

function PhotoBox({ caption }: { caption?: string }) {
  return (
    <div className={`${B}__photo`}>
      <div className={`${B}__photo-box`}>
        <PictureOutlined />
      </div>
      {caption ? <span className={`${B}__photo-cap`}>{caption}</span> : null}
    </div>
  )
}

function ExcelContent({ slide }: { slide: ExcelSlide }) {
  return (
    <div className={`${B}__sheet`}>
      <div className={`${B}__row ${B}__row--head`}>
        {slide.cols.map((c) => (
          <div key={c} className={`${B}__cell ${B}__cell--head`}>
            {c}
          </div>
        ))}
      </div>
      {slide.rows.map((row, ri) => (
        <div key={ri} className={`${B}__row`}>
          {row.map((cell, ci) => (
            <div
              key={ci}
              className={`${B}__cell${
                cell.tone && cell.tone !== 'plain'
                  ? ` ${B}__cell--${cell.tone}`
                  : ''
              }`}
            >
              {cell.photo ? <PhotoBox /> : cell.text}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function WordContent({ slide }: { slide: WordSlide }) {
  return (
    <div className={`${B}__doc`}>
      <div className={`${B}__doc-title`}>{slide.title}</div>
      <div className={`${B}__doc-meta`}>{slide.meta}</div>
      <div className={`${B}__doc-divider`} />
      {slide.blocks.map((block, bi) => (
        <div key={bi} className={`${B}__doc-block`}>
          <div className={`${B}__doc-heading`}>{block.heading}</div>
          {block.bars.map((w, i) => (
            <div
              key={i}
              className={`${B}__doc-bar`}
              style={{ width: `${w}%` }}
            />
          ))}
          {block.photos ? (
            <div className={`${B}__doc-photos`}>
              {Array.from({ length: block.photos }).map((_, pi) => (
                <PhotoBox key={pi} caption={block.photoCaption} />
              ))}
            </div>
          ) : null}
          {block.tag ? (
            <span className={`${B}__doc-tag`}>{block.tag}</span>
          ) : null}
        </div>
      ))}
      <div className={`${B}__doc-sign`}>{slide.signature}</div>
    </div>
  )
}

function PhotoContent({ slide }: { slide: PhotoSlide }) {
  return (
    <div className={`${B}__collage`}>
      <div className={`${B}__collage-title`}>{slide.title}</div>
      <div className={`${B}__collage-grid`}>
        {slide.items.map((item, i) => (
          <div key={i} className={`${B}__collage-item`}>
            <div className={`${B}__photo-box ${B}__collage-photo`}>
              <PictureOutlined />
            </div>
            <div className={`${B}__collage-loc`}>{item.location}</div>
            <div className={`${B}__collage-meta`}>
              <span className={`${B}__collage-time`}>{item.time}</span>
              {item.tag && (
                <span className={`${B}__collage-tag`}>{item.tag}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideCard({ slide }: { slide: Slide }) {
  return (
    <div className={`${B}__slide`}>
      <div className={`${B}__bar`}>
        <div className={`${B}__bar-left`}>
          <span className={`${B}__traffic ${B}__traffic--r`} />
          <span className={`${B}__traffic ${B}__traffic--y`} />
          <span className={`${B}__traffic ${B}__traffic--g`} />
          <span className={`${B}__filename`}>{slide.file}</span>
        </div>
      </div>
      <div className={`${B}__content`}>
        {slide.type === 'excel' ? (
          <ExcelContent slide={slide} />
        ) : slide.type === 'word' ? (
          <WordContent slide={slide} />
        ) : (
          <PhotoContent slide={slide} />
        )}
      </div>
      <div className={`${B}__status`}>
        <span className={`${B}__status-text`}>{slide.status}</span>
        <CheckCircleOutlined className={`${B}__status-ok`} />
      </div>
    </div>
  )
}

export default function HeroCarousel() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState<'next' | 'prev'>('next')

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection('next')
      setActiveIndex((i) => mod(i + 1, COUNT))
    }, AUTO_MS)
    return () => clearInterval(timer)
  }, [])

  const activeFormat = SLIDES[activeIndex].type

  const firstIndexOfFormat = useMemo(() => {
    const map: Record<SlideFormat, number> = { excel: -1, word: -1, photo: -1 }
    SLIDES.forEach((s, i) => {
      if (map[s.type] === -1) map[s.type] = i
    })
    return map
  }, [])

  const goPrev = () => {
    setDirection('prev')
    setActiveIndex((i) => mod(i - 1, COUNT))
  }
  const goNext = () => {
    setDirection('next')
    setActiveIndex((i) => mod(i + 1, COUNT))
  }

  const prevSlide = SLIDES[mod(activeIndex - 1, COUNT)]
  const nextSlide = SLIDES[mod(activeIndex + 1, COUNT)]

  return (
    <div className={B}>
      <div className={`${B}__tabs`}>
        {TABS.map((tab, ti) => {
          const isActive = tab.format === activeFormat
          return (
            <button
              key={ti}
              type="button"
              className={`${B}__tab${isActive ? ` ${B}__tab--active` : ''}`}
              onClick={() => {
                const target = firstIndexOfFormat[tab.format]
                if (target === -1) return
                setDirection(target >= activeIndex ? 'next' : 'prev')
                setActiveIndex(target)
              }}
            >
              <tab.icon />
              {tab.label}
            </button>
          )
        })}
      </div>

      <div className={`${B}__stage`}>
        <div className={`${B}__peek ${B}__peek--l`} aria-hidden>
          <SlideCard slide={prevSlide} />
        </div>

        <button
          type="button"
          className={`${B}__arrow ${B}__arrow--l`}
          onClick={goPrev}
          aria-label="上一个"
        >
          <LeftOutlined />
        </button>

        <div
          className={`${B}__active ${B}__active--${direction}`}
          key={activeIndex}
        >
          <SlideCard slide={SLIDES[activeIndex]} />
        </div>

        <button
          type="button"
          className={`${B}__arrow ${B}__arrow--r`}
          onClick={goNext}
          aria-label="下一个"
        >
          <RightOutlined />
        </button>

        <div className={`${B}__peek ${B}__peek--r`} aria-hidden>
          <SlideCard slide={nextSlide} />
        </div>
      </div>

      <div className={`${B}__dots`}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`${B}__dot${
              i === activeIndex ? ` ${B}__dot--active` : ''
            }`}
            onClick={() => {
              setDirection(i >= activeIndex ? 'next' : 'prev')
              setActiveIndex(i)
            }}
            aria-label={`第 ${i + 1} 个`}
          />
        ))}
      </div>

      <div className={`${B}__cap`}>
        自动轮播 · Excel 表格 / Word 汇报 / 照片拼图，多种文档都能填
      </div>
    </div>
  )
}
